import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  ensureBotConfigForRoute,
  ensureBotConfigForRoutePath,
  getBotRoutePath,
  listBotsFromBotsList,
  normalizeDirectUrlPath,
  resolveBotEntryFromRoutePath,
} from '../../config/getBotConfig';
import pathRoutes from '../../enums/pathRoutes';
import { normalizeBadgeFields } from '../admin/badgeIcon';
import { setCurrentConfig } from '../../config/currentBotConfig';
import applyTheme from '../../theme/applyTheme';
import { toggle } from '../chat/sidebar/sidebarSlice';
import BotsCarousel from '../../components/botsCarousel/BotsCarousel';
import BotsCarouselItem from '../../components/botsCarousel/botsCarouselItem/BotsCarouselItem';
import BotListPanel from './botListPanel/BotListPanel';
import ChatArea from './chatArea/ChatArea';
import MobileBotListSheet from './mobileBotListSheet/MobileBotListSheet';
import PortalHeader from './portalHeader/PortalHeader';
import carouselClasses from './botsCarousel/portalBotsCarousel.module.css';
import classes from './PortalPage.module.css';

const BOT_PORTAL_NAVIGATE = 'MYEDU_BOT_PORTAL_NAVIGATE';
const BOT_PORTAL_OPEN_LIST = 'MYEDU_BOT_PORTAL_OPEN_LIST';
const BOT_PORTAL_CLOSE_LIST = 'MYEDU_BOT_PORTAL_CLOSE_LIST';
const TABS_MEDIA = '(max-width: 900px)';
const MOBILE_MEDIA = '(max-width: 390px)';

function useMatchMedia(query) {
  const [matches, setMatches] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(query).matches
  );

  useEffect(() => {
    const media = window.matchMedia(query);
    const onChange = () => setMatches(media.matches);
    onChange();
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}

export default function PortalPage() {
  const { '*': routeSplat } = useParams();
  const routePath = normalizeDirectUrlPath(routeSplat);
  const navigate = useNavigate();
  const [configWithUrl, setConfigWithUrl] = useState(null);
  const [activeBot, setActiveBot] = useState(null);
  const [bots, setBots] = useState(null);
  const [botListOpen, setBotListOpen] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(MOBILE_MEDIA).matches
  );
  const openBotListRef = useRef(() => setBotListOpen(true));
  const dispatch = useDispatch();
  const { texts } = useSelector((state) => state.translate);
  const isTabsLayout = useMatchMedia(TABS_MEDIA);
  const isMobileLayout = useMatchMedia(MOBILE_MEDIA);

  openBotListRef.current = () => setBotListOpen(true);

  useEffect(() => {
    dispatch(toggle({ isOpen: false }));
  }, [dispatch, routePath]);

  useEffect(() => {
    setBotListOpen(isMobileLayout);
  }, [isMobileLayout]);

  useEffect(() => {
    if (configWithUrl?.theme) applyTheme(configWithUrl.theme);
  }, [configWithUrl]);

  useEffect(() => {
    if (!configWithUrl?.key) return;
    if (window.parent === window) return;
    window.parent.postMessage({
      type: BOT_PORTAL_NAVIGATE,
      key: configWithUrl.key,
    }, '*');
  }, [configWithUrl?.key]);

  useEffect(() => {
    if (!isMobileLayout) return;
    const onMessage = (event) => {
      if (!event.data || typeof event.data !== 'object') return;
      if (event.data.type === BOT_PORTAL_OPEN_LIST) {
        setBotListOpen(true);
        return;
      }
      if (event.data.type === BOT_PORTAL_CLOSE_LIST) {
        setBotListOpen(false);
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [isMobileLayout]);

  useEffect(() => {
    let cancelled = false;
    const listed = listBotsFromBotsList();
    Promise.all(
      listed.map(async (bot) => {
        try {
          const config = await ensureBotConfigForRoute(bot.projectKey, bot.botName);
          const badge = normalizeBadgeFields(config);
          const resolvedRoutePath = getBotRoutePath(
            bot.projectKey,
            bot.botName,
            config?.urlPath || bot.urlPath || bot
          );
          return {
            id: resolvedRoutePath,
            projectKey: bot.projectKey,
            botName: bot.botName,
            routePath: resolvedRoutePath,
            name: config?.headerBotName || bot.botName,
            headerBotName: config?.headerBotName || '',
            subjectName: config?.subjectName || '',
            iconUrl: config?.botIconUrl || '',
            subjectIconUrl: config?.subjectIconUrl || '',
            activeInMyEdu: config?.activeInMyEdu === true,
            approvedByPmr: badge.approvedByPmr,
            icon: badge.icon || null,
          };
        } catch {
          return {
            ...bot,
            id: bot.routePath || `${bot.projectKey}/${bot.botName}`,
            routePath: bot.routePath || `${bot.projectKey}/${bot.botName}`,
            name: bot.botName,
            activeInMyEdu: false,
            approvedByPmr: false,
            icon: null,
            subjectIconUrl: '',
          };
        }
      })
    ).then((enriched) => {
      if (cancelled) return;
      const activeBots = enriched
        .filter((bot) => bot.activeInMyEdu)
        .sort((a, b) => {
          const nameA = a.headerBotName || '';
          const nameB = b.headerBotName || '';
          return nameA.localeCompare(nameB, 'he');
        });
      setBots(activeBots);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const applyPortalConfig = useCallback((config, pathname) => {
    const next = {
      ...config,
      botUrl: window.location.origin + (pathname === '/' ? '' : pathname),
      showHeaderIcons: false,
      isPortalMobile: isMobileLayout,
      onOpenPortalBotList: () => openBotListRef.current(),
    };
    setCurrentConfig(next);
    setConfigWithUrl(next);
  }, [isMobileLayout]);

  useEffect(() => {
    if (!routePath) return undefined;
    let cancelled = false;
    setConfigWithUrl(null);
    setActiveBot(null);
    ensureBotConfigForRoutePath(routePath)
      .then((config) => {
        if (cancelled || !config) return;
        const pathname = window.location.pathname.replace(/\/$/, '') || '/';
        const entry = resolveBotEntryFromRoutePath(routePath);
        setActiveBot({
          projectKey: entry?.projectKey,
          botName: entry?.resolvedBotName || config.key,
          routePath: entry
            ? getBotRoutePath(entry.projectKey, entry.resolvedBotName, entry.bot)
            : routePath,
        });
        applyPortalConfig(config, pathname);
      })
      .catch(() => { });
    return () => { cancelled = true; };
  }, [routePath, applyPortalConfig]);

  const hasBots = bots?.length > 0;
  const activeId = activeBot?.routePath || routePath;
  const carouselItems = useMemo(() =>
    (bots || []).map((bot) => ({
      id: bot.routePath || bot.id,
      projectKey: bot.projectKey,
      botName: bot.botName,
      routePath: bot.routePath,
      iconUrl: bot.iconUrl || undefined,
      title: bot.name || undefined,
      subtitle: bot.approvedByPmr ?
        (bot.subjectName ?
          `${texts.botMapmarPrefix} ${bot.subjectName}`
          : texts.botMapmarPrefix)
        : (bot.subjectName ?
          `${texts.botSubjectPrefix} ${bot.subjectName}`
          : undefined),
      subtitleIcon: bot.subjectIconUrl || undefined,
    })),
  [bots, texts.botSubjectPrefix, texts.botMapmarPrefix]);

  const showMobileBotList = isMobileLayout && botListOpen;
  const showChat = hasBots && (!isMobileLayout || !botListOpen);
  const navigateToBot = (item) => {
    const nextPath = item.routePath || getBotRoutePath(item.projectKey, item.botName, item.urlPath);
    navigate(`/${pathRoutes.PORTAL}/${nextPath}`);
  };

  return (
    <div className={[classes.Page, isMobileLayout ? classes.MobilePage : ''].filter(Boolean).join(' ')}>
      <div className={classes.Layout}>
        <div className={classes.ChatColumn}>
          {showChat ?
            <ChatArea configWithUrl={configWithUrl} />
            : null}
        </div>
        {!isMobileLayout ?
          <div className={classes.BotsColumn}>
            <PortalHeader />
            {isTabsLayout ?
              <BotsCarousel
                className={carouselClasses.BotsCarousel}
                listClassName={carouselClasses.List}
                dividerClassName={carouselClasses.Divider}
                itemClassName={carouselClasses.Slot}
                moreClassName={carouselClasses.More}
                items={carouselItems}
                activeId={activeId}
                getItemKey={(item) => item.id}
                getItemLabel={(item) => item.title}
                onSelect={navigateToBot}
                renderItem={({ item, isActive, onSelect }) =>
                  <BotsCarouselItem
                    className={carouselClasses.BotCard}
                    iconUrl={item.iconUrl}
                    title={item.title}
                    subtitle={item.subtitle}
                    subtitleIcon={item.subtitleIcon}
                    description={item.description}
                    isActive={isActive}
                    onClick={onSelect}
                  />
                }
              />
              :
              <BotListPanel
                bots={bots || []}
                activeRoutePath={activeId}
              />
            }
          </div>
          : null}
      </div>
      {showMobileBotList ?
        <MobileBotListSheet
          bots={bots || []}
          activeRoutePath={activeId}
          onClose={() => setBotListOpen(false)}
        />
        : null}
    </div>
  );
}
